import React, { createContext, useContext, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Decode JWT payload (client-side only for UX, never trust for security)
const decodeToken = (token) => {
  // Safety check: ensure token is a valid string before processing
  if (!token || typeof token !== 'string') {
    console.error('decodeToken: Invalid token - not a string:', typeof token);
    return null;
  }
  
  try {
    const parts = token.split('.');
    if (parts.length !== 3) {
      console.error('decodeToken: Invalid JWT format - expected 3 parts, got:', parts.length);
      return null;
    }
    
    const base64Url = parts[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    return JSON.parse(jsonPayload);
  } catch (error) {
    console.error('Error decoding token:', error);
    return null;
  }
};

// Check if token is expired
const isTokenExpired = (token) => {
  // Safety check
  if (!token || typeof token !== 'string') {
    return true;
  }
  
  const decoded = decodeToken(token);
  if (!decoded || !decoded.exp) return true;
  
  // Check if token expires in next 5 minutes (refresh buffer)
  const expiryTime = decoded.exp * 1000;
  const now = Date.now();
  return expiryTime < now + (5 * 60 * 1000);
};

export const AuthProvider = ({ children }) => {
  const [token, setToken] = useState(null);
  const [user, setUser] = useState(null);
  const [allowedApps, setAllowedApps] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  // Load token from sessionStorage on mount (more secure than localStorage)
  useEffect(() => {
    const storedToken = sessionStorage.getItem('auth_token');
    const storedApps = sessionStorage.getItem('allowed_apps');
    
    if (storedToken && !isTokenExpired(storedToken)) {
      const decoded = decodeToken(storedToken);
      setToken(storedToken);
      setUser(decoded);
      
      // Load allowed apps
      if (storedApps) {
        try {
          setAllowedApps(JSON.parse(storedApps));
        } catch (e) {
          // Invalid stored data
        }
      }
    } else if (storedToken) {
      // Token expired, clear it
      sessionStorage.removeItem('auth_token');
      sessionStorage.removeItem('allowed_apps');
    }
    
    setLoading(false);
  }, []);

  const login = (newToken, apps = []) => {
    // Safety check: validate token before processing
    if (!newToken || typeof newToken !== 'string') {
      console.error('login: Invalid token provided:', typeof newToken);
      throw new Error('Invalid token - token must be a non-empty string');
    }
    
    const decoded = decodeToken(newToken);
    if (!decoded) {
      throw new Error('Invalid token - could not decode');
    }

    sessionStorage.setItem('auth_token', newToken);
    setToken(newToken);
    setUser(decoded);
    
    // Store allowed apps if provided
    const storedApps = sessionStorage.getItem('allowed_apps');
    if (storedApps) {
      try {
        setAllowedApps(JSON.parse(storedApps));
      } catch (e) {
        // Invalid stored data
      }
    }
  };

  const logout = () => {
    sessionStorage.removeItem('auth_token');
    sessionStorage.removeItem('allowed_apps');
    setToken(null);
    setUser(null);
    setAllowedApps([]);
    navigate('/login');
  };

  const updateToken = (newToken) => {
    // Safety check: validate token before processing
    if (!newToken || typeof newToken !== 'string') {
      console.error('updateToken: Invalid token provided:', typeof newToken);
      throw new Error('Invalid token - token must be a non-empty string');
    }
    
    // Used for outlet switching
    const decoded = decodeToken(newToken);
    if (!decoded) {
      throw new Error('Invalid token - could not decode');
    }

    sessionStorage.setItem('auth_token', newToken);
    setToken(newToken);
    setUser(decoded);
  };
  
  // Check if user has access to a specific app
  const hasAppAccess = (appCode) => {
    if (allowedApps.includes('*')) return true; // SUPER_ADMIN
    return allowedApps.includes(appCode);
  };
  
  // CR-P1: ORG entitlement (token claim), separate from user authorisation.
  // SUPER_ADMIN "*" in allowed_apps never implies org entitlement.
  const hasOrgModule = (appCode) => (user?.enabled_modules || []).includes(appCode);

  // Check if user can access Control Room
  const canAccessControlRoom = () => {
    return hasAppAccess('CONTROL_ROOM');
  };

  const value = {
    token,
    user,
    allowedApps,
    login,
    logout,
    updateToken,
    hasAppAccess,
    hasOrgModule,
    canAccessControlRoom,
    isAuthenticated: !!token && !isTokenExpired(token),
    loading,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
